import React from "react";
import { FiDroplet, FiAlertTriangle, FiActivity, FiDollarSign, FiZap, FiCpu, FiTrendingUp } from "react-icons/fi";
import { MdOutlineWaterDrop } from "react-icons/md";
import { KpiCard, HealthRing, MiniBar, SeverityBadge, severityColor } from "./waterHelpers";

const WmOverviewPanel = ({ d }) => {
  const s = d.summary;
  const maxWeekly = Math.max(...d.consumption.weeklyTrend.map(w => w.litres));
  const healthCol = s.waterHealthScore >= 75 ? "#10b981" : s.waterHealthScore >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div className="wm-overview">

      {/* ── Row 1: Health Ring + KPI Cards ── */}
      <div className="wmo-top-row">

        <div className="wmo-health-card">
          <div className="wmo-health-ring-wrap">
            <HealthRing value={s.waterHealthScore} size={120} stroke={12} />
            <div className="wmo-ring-center">
              <span className="wmo-ring-num" style={{ color: healthCol }}>{s.waterHealthScore}</span>
              <span className="wmo-ring-lbl">Water Health</span>
            </div>
          </div>
          <div className="wmo-health-meta">
            <div className="wmo-hm-row">
              <span>Available</span>
              <strong style={{ color: "#3b82f6" }}>{(s.totalWaterAvailable / 1000).toFixed(0)} KL</strong>
            </div>
            <MiniBar value={s.totalWaterAvailable} max={s.totalCapacity} color="#3b82f6" height={6} />
            <div className="wmo-hm-row" style={{ marginTop: 8 }}>
              <span>Leakage Suspected</span>
              <strong style={{ color: s.leakageSuspected > 0 ? "#ef4444" : "#10b981" }}>{s.leakageSuspected} zones</strong>
            </div>
            <div className="wmo-hm-row">
              <span>Recycled Water</span>
              <strong style={{ color: "#8b5cf6" }}>{s.recycledWaterPct}%</strong>
            </div>
            <div className="wmo-hm-row">
              <span>Days Remaining</span>
              <strong style={{ color: s.daysOfWaterRemaining < 7 ? "#ef4444" : "#10b981" }}>{s.daysOfWaterRemaining} days</strong>
            </div>
          </div>
        </div>

        <div className="wmo-kpi-grid">
          <KpiCard label="Water Available"   value={`${(s.totalWaterAvailable/1000).toFixed(0)} KL`}  color="#3b82f6" icon={MdOutlineWaterDrop} sub={`${s.availabilityPct}% of ${(s.totalCapacity/1000).toFixed(0)} KL`} />
          <KpiCard label="Today's Use"       value={`${(s.todayConsumption/1000).toFixed(1)} KL`}     color="#0ea5e9" icon={FiDroplet}          sub={`↑ ${(((s.todayConsumption-s.yesterdayConsumption)/s.yesterdayConsumption)*100).toFixed(0)}% vs yesterday`} />
          <KpiCard label="Monthly Use"       value={`${(s.monthlyConsumption/1000).toFixed(0)} KL`}   color="#6366f1" icon={FiActivity}         sub={`₹${(s.estimatedMonthlyCost/1000).toFixed(0)}k est. cost`} />
          <KpiCard label="Active Alerts"     value={s.criticalAlerts + s.warningAlerts}               color={s.criticalAlerts > 0 ? "#ef4444" : "#f59e0b"} icon={FiAlertTriangle} sub={`${s.criticalAlerts} critical · ${s.warningAlerts} warning`} />
          <KpiCard label="Active Pumps"      value={`${s.activePumps} / ${s.totalPumps}`}             color="#10b981" icon={FiZap}              sub="Running now" />
          <KpiCard label="Monthly Cost"      value={`₹${(s.estimatedMonthlyCost/1000).toFixed(0)}k`} color="#ec4899" icon={FiDollarSign}       sub="Estimated" />
          <KpiCard label="Per Capita / Day"  value={s.consumptionPerCapita}                           unit="L"        color="#f59e0b" icon={FiTrendingUp} sub={`${d.site.totalResidents} residents`} />
          <KpiCard label="Recycled Water"    value={`${s.recycledWaterPct}%`}                         color="#8b5cf6" icon={FiCpu}              sub="STP output used" />
        </div>
      </div>

      {/* ── Row 2: AI Insights + Alerts + Weekly Chart ── */}
      <div className="wmo-bottom-row">

        <div className="wmo-card">
          <div className="wmo-card-title"><FiCpu size={14} /> AI Insights</div>
          {s.aiInsights.map((msg, i) => (
            <div key={i} className="wmo-ai-item"><span className="wmo-ai-dot" />  {msg}</div>
          ))}
        </div>

        <div className="wmo-card">
          <div className="wmo-card-title">
            <FiAlertTriangle size={14} /> Active Alerts
            <span className="wmo-alert-chip" style={{ background: "#fee2e2", color: "#ef4444" }}>{s.criticalAlerts} Critical</span>
          </div>
          {d.alerts.slice(0, 5).map(a => (
            <div key={a.id} className="wmo-alert-row">
              <div className="wmo-alert-dot" style={{ background: severityColor(a.severity) }} />
              <div className="wmo-alert-info">
                <span className="wmo-alert-loc">{a.location}</span>
                <span className="wmo-alert-msg">{a.message}</span>
              </div>
              <SeverityBadge severity={a.severity} />
            </div>
          ))}
        </div>

        <div className="wmo-card">
          <div className="wmo-card-title"><FiActivity size={14} /> Weekly Consumption</div>
          <div className="wmo-bar-chart">
            {d.consumption.weeklyTrend.map(w => (
              <div key={w.d} className="wmo-bar-col">
                <div className="wmo-bar-wrap">
                  <div className="wmo-bar" style={{ height: `${(w.litres / maxWeekly) * 100}%` }} />
                </div>
                <span className="wmo-bar-day">{w.d}</span>
                <span className="wmo-bar-val">{(w.litres / 1000).toFixed(1)}k</span>
              </div>
            ))}
          </div>
          <div className="wmo-card-title" style={{ marginTop: "0.75rem" }}><FiDroplet size={14} /> Block-wise Today</div>
          {d.consumption.blockWise.map(b => (
            <div key={b.block} className="wmo-block-row">
              <span className="wmo-block-label">Block {b.block}</span>
              <div style={{ flex: 1 }}>
                <MiniBar value={b.pct} color="#3b82f6" height={8} />
              </div>
              <span className="wmo-block-val">{(b.litres / 1000).toFixed(2)} KL · {b.pct}%</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default WmOverviewPanel;
